<template>
  <div class="trading-opration-item">
    <!-- 杠杆 -->
    <div class="lever-box">
      <label>{{ $t('选择杠杆') }}</label>
      <div
        v-for="item in levers"
        :key="item"
        :class="['lever-item', { active: item === checkedLever }]"
        @click="checkedLever = item"
      >
        {{ item }}X
      </div>
    </div>

    <!-- 价格 -->
    <div class="price-box">
      <label>{{ priceText }}</label>
      <input
        v-if="currentTradingType === 'limit'"
        v-model="price"
        class="price-input"
        @input="handleInput(price, 'price', digits.priceDigit)"
      />
      <div v-else class="market-price-box">
        {{
          {
            BULL: this.$t('以当前最优价格买入'),
            BEAR: this.$t('以当前最优价格卖出'),
          }[this.side]
        }}
      </div>
      <el-popover
        v-if="holdingData.length"
        placement="bottom-end"
        v-model="popoverVisible"
        popper-class="coin-popover"
        :visible-arrow="false"
      >
        <div
          v-for="item in holdingData"
          :key="item.currencyName"
          class="coin-item"
          :class="{ active: item.currencyName === checkedCurrency }"
          @click="
            ;(checkedCurrency = item.currencyName), (popoverVisible = false)
          "
        >
          {{ item.currencyName }}
        </div>

        <div slot="reference" class="coin-popover--reference">
          <span> {{ checkedCurrency }} </span>
          <img :src="require('@/assets/images/arrow.png')" />
        </div>
      </el-popover>
    </div>

    <!-- 数量 -->
    <div class="num-box">
      <label class="num-text">{{ numText }}</label>
      <div class="num-input-box">
        <input
          v-model="num"
          class="num-input"
          @input="handleInput(num, 'num', digits.numDigit)"
        />
        <span>{{ coin }}</span>
      </div>
    </div>

    <!-- 滑块 -->
    <div
      class="slider-box"
      :class="`slider_box_${{ BULL: 'buy', BEAR: 'sell' }[side]}`"
    >
      <el-slider
        v-model="percent"
        :marks="marks"
        :disabled="isSliderDisabled"
        :format-tooltip="sliderTips"
        :class="{
          second_stop_active: percent > 25,
          third_stop_active: percent > 50,
          fourth_stop_active: percent > 75,
        }"
        @change="onSliderChange"
      ></el-slider>
    </div>

    <!-- 数据及止盈止损 -->
    <div class="data-and-stop">
      <div class="data-box">
        <div class="row">
          <div class="cell title">{{ $t('可用保证金') }}</div>
          <div class="cell value">
            <span>{{ freeMargin || '---' }} {{ checkedCurrency }}</span>
            <img
              v-if="isSignIn"
              :src="require('@/assets/images/transfer1.png')"
              @click="transferModalVisible = true"
            />
          </div>
        </div>
        <div class="row">
          <div class="cell title">{{ $t('预估保证金') }}</div>
          <div class="cell value">
            {{ feeAndBonds.bonds || '---' }} {{ checkedCurrency }}
          </div>
        </div>
        <div class="row">
          <div class="cell title">{{ $t('手续费') }}</div>
          <div class="cell value">
            {{ feeAndBonds.fee || '---' }} {{ checkedCurrency }}
          </div>
        </div>
      </div>
      <div v-if="isSignIn" class="stop-box">
        <label>{{ $t('止盈止损') }}</label>
        <img
          :src="
            require(`@/assets/images/${
              isStopDataEntered ? 'checked' : 'unchecked'
            }.png`)
          "
          @click="handleStopClicked"
        />
      </div>
    </div>

    <!-- 提交 -->
    <div class="commit-box">
      <el-button
        v-if="isSignIn"
        :loading="loading"
        :type="side === 'BULL' ? 'primary' : 'danger'"
        @click="handleCommit"
      >
        {{ buttonText }}
      </el-button>
      <el-button v-else class="not-logged-in-box">
        <span @click="jumpTo('/signin')">{{ $t('登录') }}</span>
        <span> {{ $t('或') }} </span>
        <span @click="jumpTo('/register')">{{ $t('注册') }}</span>
      </el-button>
    </div>

    <!-- 止盈止损Modal -->
    <Modal
      v-model="dialogVisible"
      :title="$t('止盈止损')"
      width="388px"
      :closable="false"
      :mask-closable="false"
      class-name="common-modal"
    >
      <div class="stop-item">
        <div class="input-box">
          <div class="label-text">{{ $t('止盈') }}</div>
          <input
            v-model="profitPrice"
            :placeholder="$t('请输入止盈价格')"
            @input="handleInput(profitPrice, 'profitPrice', digits.priceDigit)"
            @blur="!+profitPrice && (profitPrice = '')"
          />
        </div>
        <div class="price-value">
          <span>
            {{ `${{ BULL: '&gt;', BEAR: '&lt;' }[this.side]} ${actualPrice}` }}
          </span>
          <span v-if="!!expectedProfit">
            {{ $t('预计盈利') }} {{ expectedProfit }}
          </span>
        </div>
      </div>
      <div class="stop-item">
        <div class="input-box">
          <div class="label-text">{{ $t('止损') }}</div>
          <input
            v-model="lossPrice"
            :placeholder="$t('请输入止损价格')"
            @input="handleInput(lossPrice, 'lossPrice', digits.priceDigit)"
            @blur="!+lossPrice && (lossPrice = '')"
          />
        </div>
        <div class="price-value">
          <span>
            {{ `${{ BULL: '&lt;', BEAR: '&gt;' }[this.side]} ${actualPrice}` }}
          </span>
          <span v-if="!!expectedLoss">
            {{ $t('预计亏损') }} {{ expectedLoss }}
          </span>
        </div>
      </div>
      <span slot="footer">
        <Button type="text" @click="dialogVisible = false">
          {{ $t('取消') }}
        </Button>
        <Button type="primary" @click="handleSetStop">
          {{ $t('确认') }}
        </Button>
      </span>
    </Modal>

    <!-- 划转Modal -->
    <TransferModal
      v-model="transferModalVisible"
      :coinsAsset="coinsAsset"
      :holdingData="holdingData"
      :initCurrency="checkedCurrency"
      @transferSucceed="$emit('transferSucceed')"
    />
  </div>
</template>
<script>
import { mapGetters, mapState } from 'vuex'
import { Big } from 'big.js'
import digitProcess from '@/mixin/digital-process'
import { doLimitTrade, doMarketTrade } from '@/api/contract'
import EventBus from '@/utils/event-bus'
import TransferModal from '@/components/TransferModal'

export default {
  mixins: [digitProcess],
  components: {
    TransferModal,
  },
  props: {
    // 'limit' || 'market'
    currentTradingType: String,
    // 'BULL' || 'BEAR'
    side: String,
    coinsAsset: Array, // 币币资产
    holdingData: Array,
    contractList: Array,
  },
  data() {
    return {
      transferModalVisible: false,
      // 是否输入了止盈止损价格
      isStopDataEntered: false,
      // 止盈价格
      profitPrice: '',
      // 止损价格
      lossPrice: '',
      // 止盈止损 dialog 显示开关
      dialogVisible: false,
      // currency popover 显示开关
      popoverVisible: false,
      loading: false,
      // 币种
      checkedCurrency: '',
      // 杠杆
      checkedLever: '',
      // 滑块百分比
      percent: 0,
      // 滑块节点
      marks: {
        0: '',
        25: '',
        50: '',
        75: '',
        100: '',
      },
      // 数量
      num: '',
      // 价格
      price: '',
    }
  },
  watch: {
    handicapFirstPrice(newVal) {
      this.price =
        newVal[{ BULL: 'buyFirstPrice', BEAR: 'sellFirstPrice' }[this.side]]
    },
    // 设置默认currency为持仓列表中的首值
    holdingData(newV) {
      this.checkedCurrency = newV[0].currencyName
    },
    // 可用保证金 & 数量 & 实际价格(用户输入 or 最新价) & 杠杆变化时,
    // 重新设置滑块百分比
    freeMargin() {
      this.setPercent()
    },
    num() {
      this.setPercent()
    },
    actualPrice() {
      this.setPercent()
    },
    checkedLever() {
      this.setPercent()
    },
    // 选择的合约发生变化时，对一些数据进行重置
    contractNo: {
      handler() {
        this.checkedLever = ''
        this.lossPrice = ''
        this.profitPrice = ''
        this.isStopDataEntered = false
        this.num = ''
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('contract', ['contractData', 'quotes', 'handicapFirstPrice']),
    ...mapGetters(['isSignIn']),
    contractNo() {
      return this.contractData.contractNo
    },
    coin() {
      return this.contractData.coin
    },
    // 最新行情价
    newestPrice() {
      return this.contractData.price
    },
    digits() {
      return this.coinsAsset.reduce(
        (calc, item) => {
          if (item.coinName === this.checkedCurrency) {
            calc.priceDigit = item.showDigits
          }
          if (item.coinName === this.coin) {
            calc.numDigitt = item.showDigits
          }
          return calc
        },
        {
          priceDigit: 4, // 价格精度
          numDigit: 4, // 数量精度
        }
      )
    },
    // 可用保证金
    freeMargin() {
      if (!this.quotes.length || !this.holdingData.length) return ''
      // 获取前选中Curreny的持仓数据
      const currencyHoldingData = this.holdingData.find(
        (item) => item.currencyName === this.checkedCurrency
      )
      if (!currencyHoldingData) return ''
      // positionsDataList 当前选中Curreny持仓数据列表，used 已用 ， balance  余额
      const { positionsDataList, used, balance } = currencyHoldingData
      if (!positionsDataList.length) return Big(balance).sub(used).toString()
      // 1、 总浮动盈亏 = （当前价 - 开仓价）* 方向数字 * 合约单位 * 数量
      const totalFloatingPhase = positionsDataList.reduce((calc, item) => {
        const quote = this.quotes.find(
          (_item) => _item.contractNo === item.contractNo.toUpperCase()
        )
        if (quote) {
          return Big(quote.price)
            .minus(item.openPrice)
            .mul(item.direction.toUpperCase() === 'BULL' ? 1 : -1)
            .mul(item.unit)
            .mul(item.amount)
            .plus(calc)
        }
        return calc
      }, Big(0))
      // 2、 净值 = 总浮动盈亏 + 余额
      // 3、 可用 = 净值 - 已用
      return totalFloatingPhase.plus(balance).sub(used).toString()
    },
    // 选中的合约 + 选择的currency -> 合约信息
    contractInfo() {
      return this.contractList.find(
        (item) =>
          item.contractNo.toUpperCase() === this.contractNo &&
          this.checkedCurrency === item.currency
      )
    },
    // 杠杆
    levers() {
      return this.contractInfo?.levers?.split?.(',') || []
    },
    // 预计亏损
    expectedLoss() {
      return this.calculateExpected('lossPrice')
    },
    // 预计盈利
    expectedProfit() {
      return this.calculateExpected('profitPrice')
    },
    // 手续费 & 预估保证金
    feeAndBonds() {
      const ret = {
        fee: '',
        bonds: '',
      }

      // 未选择杠杆 | 合约信息不存在 | 价格未输入或不存在 | 数量未输入时，置空
      if (
        !this.checkedLever ||
        !this.contractInfo ||
        !+this.num ||
        !+this.actualPrice
      ) {
        return ret
      }
      const baseVal = Big(this.actualPrice) // 用户输入价格 | 最新行情价
        .times(this.num)
        .times(this.contractInfo.unit)
      ret.fee = this.truncateDigit(
        baseVal
          .times(this.contractInfo.openCommissionRate /* 开仓手续费费率 */)
          .toString(),
        this.digits.priceDigit
      )
      ret.bonds = this.truncateDigit(
        baseVal.div(this.checkedLever).toString(),
        this.digits.priceDigit
      )
      return ret
    },
    // 滑块是否禁用
    isSliderDisabled() {
      return (
        !+this.freeMargin || // 无可用保证金
        !this.contractInfo || // 合约信息不存在
        !this.checkedLever || // 未选择杠杆
        !+this.actualPrice // 用户输入价格 | 最新行情价 不存在或未输入
      )
    },
    priceText() {
      return {
        BULL: this.$t('买入价'),
        BEAR: this.$t('卖出价'),
      }[this.side]
    },
    numText() {
      return {
        BULL: this.$t('买入量'),
        BEAR: this.$t('卖出量'),
      }[this.side]
    },
    buttonText() {
      return {
        BULL: this.$t('买入xxx', { coin: this.coin }),
        BEAR: this.$t('卖出xxx', { coin: this.coin }),
      }[this.side]
    },
    // 实际价格：限价使用用户输入价格，市价取合约当前行情最新价格
    actualPrice() {
      return this.currentTradingType === 'limit' ? this.price : this.newestPrice
    },
  },
  methods: {
    /**
     * @description 设置滑块百分比
     */
    setPercent() {
      if (this.isSliderDisabled) {
        this.percent = 0
        return
      }
      this.percent = +this.truncateDigit(
        Big(this.num || 0) // 数量
          .mul(this.actualPrice) // 用户输入价格 | 最新行情价
          .div(this.freeMargin) // 可用保证金
          .div(this.contractInfo.unit) // 合约单位
          .div(this.checkedLever) // 杠杆
          .toString(),
        2
      )
    },
    /**
     * @description 处理确认设置止盈止损
     */
    handleSetStop() {
      this.isStopDataEntered = !!(this.profitPrice || this.lossPrice)
      this.dialogVisible = false
    },
    /**
     * @description 计算预计盈利&预计亏损
     * @param priceField 止盈止损价格字段
     *
     */
    calculateExpected(priceField) {
      const price = this[priceField]

      if (!+price || !this.contractInfo) return ''

      let dValue
      if (
        (priceField === 'profitPrice' && this.side === 'BULL') ||
        (priceField === 'lossPrice' && this.side === 'BEAR')
      ) {
        dValue = Big(price).minus(this.actualPrice)
      } else {
        dValue = Big(this.actualPrice).minus(price)
      }
      if (dValue.lte(0)) {
        return ''
      }

      return this.truncateDigit(
        dValue.mul(this.num).mul(this.contractInfo.unit).toString(),
        this.digits.priceDigit
      )
    },
    handleStopClicked() {
      if (!+this.actualPrice) {
        return this.$Message.warning(this.$t('请输入') + this.priceText)
      }
      if (!+this.num) {
        return this.$Message.warning(this.$t('请输入') + this.numText)
      }

      this.dialogVisible = true
    },
    jumpTo(p, q) {
      this.$router.push({ path: p, query: q })
    },
    async handleCommit() {
      if (!this.checkedLever) {
        return this.$Message.warning(this.$t('请选择杠杆'))
      }
      if (!+this.actualPrice && this.currentTradingType === 'limit') {
        return this.$Message.warning(this.$t('请输入') + this.priceText)
      }
      if (!+this.num) {
        return this.$Message.warning(this.$t('请输入') + this.numText)
      }

      const api = { limit: doLimitTrade, market: doMarketTrade }[
        this.currentTradingType
      ]
      this.loading = true
      try {
        await api({
          contractNo: this.contractNo.toLowerCase(),
          direction: this.side,
          currency: this.checkedCurrency,
          openPrice: this.actualPrice,
          amount: this.num,
          lever: this.checkedLever,
          profitPrice: this.profitPrice || undefined,
          lossPrice: this.lossPrice || undefined,
        })
        this.$Message.warning(this.$t('操作成功'))
        // 合约交易成功，需更新EntrustDetail组件列表数据 & TradingOperation组件holdingData(持仓数据)
        EventBus.$emit('contractTradeSucceed')
      } finally {
        this.loading = false
      }
    },
    sliderTips(val) {
      return val + '%'
    },
    onSliderChange(val) {
      // 拉动滑块设置数量
      this.num = this.truncateDigit(
        Big(this.freeMargin) // 可用保证金
          .mul(this.contractInfo.unit) // 合约单位
          .mul(this.checkedLever) // 杠杆
          .mul(val) // 滑块百分比
          .div(this.actualPrice) // 用户输入价格 | 最新行情价
          .toString(),
        this.digits.numDigit
      )
    },
  },
}
</script>
<style scoped lang="less">
.trading-opration-item {
  flex: 1;
  .lever-box {
    margin-top: 10px;
    display: flex;
    align-items: center;
    label {
      height: 17px;
      font-size: 12px;
      font-family: PingFang SC, PingFang SC;
      font-weight: 400;
      color: #8a91ab;
      line-height: 14px;
    }
    .lever-item {
      width: 42px;
      text-align: center;
      height: 21px;
      line-height: 21px;
      border-radius: 4px;
      border: 1px solid #333646;
      margin-left: 8px;
      cursor: pointer;

      &.active {
        background-color: #b0b8db;
      }
    }
  }
  .price-box {
    margin-top: 20px;
    display: flex;
    align-items: center;
    label {
      white-space: nowrap;
      font-size: 14px;
      font-weight: 400;
      color: #a9b2d7;
      line-height: 16px;
      margin-right: 5px;
    }
    input {
      width: 100px;
    }
    .price-input,
    .market-price-box {
      flex: 1;
      height: 30px;
      border-radius: 2px;
      border: 1px solid #484b5b;

      font-size: 12px;
      font-weight: 400;
      color: #a9b2d7;
    }
    .price-input {
      outline: none;
      background: #101424;
      padding: 0 4px;
    }
    .market-price-box {
      line-height: 30px;
      text-align: center;
    }
  }
  .num-box {
    margin-top: 10px;
    display: flex;
    align-items: center;
    label {
      white-space: nowrap;
      font-size: 14px;
      font-weight: 400;
      color: #a9b2d7;
      line-height: 16px;
      margin-right: 5px;
    }
    .num-input-box {
      flex: 1;
      border: 1px solid #484b5b;
      padding-right: 3px;
      display: flex;
      align-items: center;
      span {
        font-size: 14px;
        font-weight: 400;
        color: #aab2d4;
      }
      .num-input {
        flex: 1;
        outline: none;
        border: none;
        height: 30px;
        background: #101424;
        font-size: 12px;
        font-weight: 400;
        color: #a9b2d7;
        padding: 0 4px;
      }
    }
  }
  .data-and-stop {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
    .data-box {
      display: table;

      .row {
        display: table-row;
        text-align: right;
        .cell {
          padding-bottom: 4px;
          display: table-cell;
          font-size: 12px;
          font-weight: 400;
          line-height: 14px;
          &.title {
            color: #536078;
          }
          &.value {
            text-align: left;
            padding-left: 22px;
            color: #9b9fae;

            img {
              position: relative;
              top: -2px;
              width: 16px;
              height: 16px;
              margin-left: 8px;
              cursor: pointer;
            }
          }
        }
      }
    }
    .stop-box {
      label {
        height: 20px;
        font-size: 14px;
        font-weight: 400;
        color: #aab2d4;
        line-height: 16px;
        margin-right: 8px;
      }
      img {
        cursor: pointer;
        width: 16px;
        height: 16px;
      }
    }
  }

  .commit-box {
    padding: 0 10px;
    .commit-btn {
      text-align: center;
      height: 40px;
      line-height: 40px;

      border-radius: 2px;
      cursor: pointer;

      font-size: 14px;
      font-weight: 400;
      color: #ffffff;

      &.sell {
        background: #f56c6c;
      }
      &.buy {
        background: #41b37e;
      }
    }
  }
}
</style>
